/**
 * User Directory Component
 *
 * Search and discover users across the network.
 */
import { useState, useMemo } from 'react';
import { Avatar } from './Avatar';
import './UserDirectory.css';

export interface UserInfo {
  pubkey: string;
  displayName: string;
  bio?: string;
  avatarUrl?: string;
  karma?: number;
  isFriend?: boolean;
  isBlocked?: boolean;
  isOnline?: boolean;
}

interface UserDirectoryProps {
  /** All known users */
  users: UserInfo[];
  /** Current user's pubkey */
  currentUserPubkey?: string;
  /** Add friend handler */
  onAddFriend: (pubkey: string) => void;
  /** Remove friend handler */
  onRemoveFriend: (pubkey: string) => void;
  /** Block user handler */
  onBlockUser: (pubkey: string) => void;
  /** View profile handler */
  onViewProfile: (pubkey: string) => void;
  /** Start chat handler */
  onStartChat?: (pubkey: string) => void;
  /** Close handler */
  onClose: () => void;
}

type SortBy = 'name' | 'karma' | 'online';
type FilterBy = 'all' | 'friends' | 'online';

export function UserDirectory({
  users,
  currentUserPubkey,
  onAddFriend,
  onRemoveFriend,
  onBlockUser,
  onViewProfile,
  onStartChat,
  onClose,
}: UserDirectoryProps) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [filterBy, setFilterBy] = useState<FilterBy>('all');

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = users.filter(u => u.pubkey !== currentUserPubkey && !u.isBlocked);

    // Apply search
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter(u =>
        u.displayName.toLowerCase().includes(query) ||
        u.pubkey.toLowerCase().includes(query) ||
        u.bio?.toLowerCase().includes(query)
      );
    }

    // Apply filter
    switch (filterBy) {
      case 'friends':
        result = result.filter(u => u.isFriend);
        break;
      case 'online':
        result = result.filter(u => u.isOnline);
        break;
    }

    // Apply sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.displayName.localeCompare(b.displayName));
        break;
      case 'karma':
        result.sort((a, b) => (b.karma ?? 0) - (a.karma ?? 0));
        break;
      case 'online':
        result.sort((a, b) => {
          if (a.isOnline && !b.isOnline) return -1;
          if (!a.isOnline && b.isOnline) return 1;
          return a.displayName.localeCompare(b.displayName);
        });
        break;
    }

    return result;
  }, [users, currentUserPubkey, search, sortBy, filterBy]);

  // Stats
  const stats = useMemo(() => ({
    total: users.filter(u => u.pubkey !== currentUserPubkey && !u.isBlocked).length,
    friends: users.filter(u => u.isFriend).length,
    online: users.filter(u => u.isOnline).length,
  }), [users, currentUserPubkey]);

  return (
    <div className="user-directory">
      <div className="directory-header">
        <h2>User Directory</h2>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="directory-search">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search users..."
          className="search-input"
        />
      </div>

      <div className="directory-filters">
        <div className="filter-group">
          <label>Show:</label>
          <select value={filterBy} onChange={e => setFilterBy(e.target.value as FilterBy)}>
            <option value="all">All Users ({stats.total})</option>
            <option value="friends">Friends ({stats.friends})</option>
            <option value="online">Online ({stats.online})</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Sort:</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as SortBy)}>
            <option value="name">Name</option>
            <option value="karma">Karma</option>
            <option value="online">Online First</option>
          </select>
        </div>
      </div>

      <div className="directory-stats">
        <span>{filteredUsers.length} users</span>
        {search && <span className="search-hint">matching "{search}"</span>}
      </div>

      <div className="user-list">
        {filteredUsers.length === 0 ? (
          <div className="no-users">
            {search ? (
              <p>No users found matching "{search}"</p>
            ) : filterBy === 'friends' ? (
              <p>No friends yet</p>
            ) : filterBy === 'online' ? (
              <p>No users online</p>
            ) : (
              <p>No users found</p>
            )}
          </div>
        ) : (
          filteredUsers.map(user => (
            <UserCard
              key={user.pubkey}
              user={user}
              onAddFriend={onAddFriend}
              onRemoveFriend={onRemoveFriend}
              onBlockUser={onBlockUser}
              onViewProfile={onViewProfile}
              onStartChat={onStartChat}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface UserCardProps {
  user: UserInfo;
  onAddFriend: (pubkey: string) => void;
  onRemoveFriend: (pubkey: string) => void;
  onBlockUser: (pubkey: string) => void;
  onViewProfile: (pubkey: string) => void;
  onStartChat?: (pubkey: string) => void;
}

function UserCard({
  user,
  onAddFriend,
  onRemoveFriend,
  onBlockUser,
  onViewProfile,
  onStartChat,
}: UserCardProps) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="user-card">
      <div className="user-card-main" onClick={() => onViewProfile(user.pubkey)}>
        <div className="avatar-with-status">
          <Avatar
            src={user.avatarUrl}
            displayName={user.displayName}
            size="medium"
          />
          {user.isOnline && <span className="online-indicator" />}
        </div>
        <div className="user-info">
          <div className="user-name">
            {user.displayName}
            {user.isFriend && <span className="friend-badge">Friend</span>}
          </div>
          {user.bio && <div className="user-bio">{user.bio}</div>}
          <div className="user-meta">
            <span className="user-pubkey">{user.pubkey.slice(0, 8)}...</span>
            {user.karma !== undefined && (
              <span className="user-karma">
                {user.karma >= 0 ? '+' : ''}{user.karma} karma
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="user-card-actions">
        {user.isFriend ? (
          <>
            {onStartChat && (
              <button
                className="action-btn chat"
                onClick={() => onStartChat(user.pubkey)}
                title="Message"
              >
                💬
              </button>
            )}
            <button
              className="action-btn more"
              onClick={() => setShowActions(!showActions)}
              title="More actions"
            >
              ⋯
            </button>
          </>
        ) : (
          <button
            className="action-btn add-friend"
            onClick={() => onAddFriend(user.pubkey)}
            title="Add friend"
          >
            +
          </button>
        )}

        {showActions && (
          <div className="actions-dropdown">
            <button onClick={() => { onRemoveFriend(user.pubkey); setShowActions(false); }}>
              Remove Friend
            </button>
            <button className="danger" onClick={() => { onBlockUser(user.pubkey); setShowActions(false); }}>
              Block User
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
